import {
  StockEnum,
  MoodEnum,
  PropertyTypeEnum,
  ExperienceTypeEnum,
  HolidayPackageTypeEnum,
  HolidayPackageBookingTypeEnum
} from './enums';
import type { TwMainColor, PostType } from './types';

export interface CustomLink {
  label?: string
  href: string
  targetBlank?: boolean
  image?: string
  isExternal?: boolean
  subText?: string
  subTextHref?: string
}

export interface GuestsObject {
  adults?: number;
  children?: number;
  infants?: number;
}

export interface TaxonomyType {
  id: string | number;
  name: string;
  href: string;
  cover?: string;
  count?: number;
  thumbnail?: string;
  desc?: string;
  shortDescription?: string;
  color?: TwMainColor | string;
  taxonomy: 'category' | 'tag';
  listingType?: 'stay' | 'experiences';
}

export interface AuthorType {
  id: string | number;
  firstName: string;
  lastName: string;
  displayName: string;
  avatar: string;
  bgImage?: string;
  email?: string;
  count: number;
  desc: string;
  jobName: string;
  href: string;
  starRating?: number;
}

export interface PostDataType {
  id: string | number;
  author: AuthorType;
  date: string;
  href: string;
  categories: TaxonomyType[];
  title: string;
  featuredImage: string;
  desc?: string;
  commentCount: number;
  viewCount: number;
  readingTime: number;
  postType?: PostType;
}

///
export interface StayDataType {
  property_uuid: string | number;
  author: AuthorType;
  date: string;
  borgo_name?: string;
  href: string;
  name: string;
  featuredImage: string;
  commentCount: number;
  viewCount: number;
  address: string;
  reviewStart: number;
  reviewCount: number;
  like: boolean;
  cover: string;
  place_cover: string;
  galleryImgs: string[];
  price: string;
  listingCategory: TaxonomyType;
  maxGuests: number;
  bedrooms: number;
  bathrooms: number;
  saleOff?: string | null;
  isAds: boolean | null;
  latitude: number;
  longitude: number;
  gallery_images?: string[];
  region?: string;
  city?: string;
  mood?: MoodEnum;
  property_type?: PropertyTypeEnum;
  only_in_package?: boolean;
};

///
export interface ExperiencesDataType {
  experience_uuid: string | number;
  author: AuthorType;
  date: string;
  href: string;
  name: string;
  featuredImage: string;
  commentCount: number;
  viewCount: number;
  address: string;
  reviewStart: number;
  reviewCount: number;
  like: boolean;
  cover: string;
  price: string;
  listingCategory: TaxonomyType;
  maxGuests: number;
  saleOff?: string | null;
  isAds: boolean | null;
  gallery_images?: string[];
  latitude: number;
  longitude: number;
  minutes_duration: number;
  region?: string;
  city?: string;
  mood?: MoodEnum;
  type?: ExperienceTypeEnum;
  only_in_package?: boolean;
};

export interface HolidayPackageDataType {
  package_uuid: string | number;
  slug?: string;
  host_uuid: string;
  name: string;
  description?: string;
  tags?: string[];
  mood?: MoodEnum;
  duration_days: number;
  fixed_dates_ongoing: boolean;
  flexible_dates: boolean;
  flexible_start_date?: string;
  flexible_end_date?: string;
  fixed_dates: boolean;
  fixed_start_dates?: string[];
  fixed_end_dates?: string[];
  package_type: HolidayPackageBookingTypeEnum;
  defined_guests?: number;
  min_guests?: number;
  max_guests?: number;
  latest_booking?: string;
  cover_image: string;
  is_deleted?: boolean;
  is_published?: boolean;
  package_content?: any[];
  created_at?: string;
  updated_at?: string;
  gallery_images?: string[];
  price: number;
  currency: string;

  // Additional fields not in DB but used in frontend
  location?: string;
  latitude?: number;
  longitude?: number;
  region?: string;
  image?: string;
  category?: HolidayPackageTypeEnum; // Frontend category type (adventure, family, etc.)
  rating?: number;
  reviews?: number;
  total_reviews?: number;
}

export type StockElementType = StayDataType | ExperiencesDataType | HolidayPackageDataType;

// Use typeguard to check the type
export function isStay(item: StockElementType): item is StayDataType {
  return (item as StayDataType).property_uuid !== undefined;
}

export function isExperience(item: StockElementType): item is ExperiencesDataType {
  return (item as ExperiencesDataType).experience_uuid !== undefined;
}

export function isHolidayPackage(item: StockElementType): item is HolidayPackageDataType {
  return (item as HolidayPackageDataType).package_uuid !== undefined;
}

interface BookingBase {
  id: string | number;
  startDate: string;
  endDate: string;
  guests: number;
  price: number;
  additional_fees: number;
}

export interface BookingStay extends BookingBase {
  type: StockEnum.STAY;
  item: StayDataType;
}

export interface BookingExperience extends BookingBase {
  type: StockEnum.EXPERIENCE;
  item: ExperiencesDataType;
}

export interface BookingHolidayPackage extends BookingBase {
  type: StockEnum.HOLIDAY_PACKAGE;
  item: HolidayPackageDataType;
}

export type BookingDataType = BookingStay | BookingExperience | BookingHolidayPackage;